<template>
  <div class="engagement-heatmap">
    <!-- Y-axis: hours of day -->
    <div class="heatmap-grid">
      <!-- Day-of-week header row -->
      <div class="cell cell--header"></div>
      <div
        v-for="day in DAYS"
        :key="day.key"
        class="cell cell--header"
      >
        {{ day.label }}
      </div>

      <!-- Hour rows -->
      <template v-for="hour in HOURS" :key="hour">
        <div class="cell cell--hour-label">{{ formatHour(hour) }}</div>
        <div
          v-for="day in DAYS"
          :key="`${hour}-${day.key}`"
          class="cell cell--data"
          :style="{ background: cellColor(hour, day.key) }"
          :title="`${day.label} ${formatHour(hour)}: ${cellCount(hour, day.key)} events`"
        ></div>
      </template>
    </div>

    <!-- Legend -->
    <div class="heatmap-legend">
      <span class="legend-label">Less</span>
      <div v-for="step in LEGEND_STEPS" :key="step" class="legend-cell" :style="{ background: legendColor(step) }"></div>
      <span class="legend-label">More</span>
    </div>

    <p v-if="isEmpty" class="no-data">No activity data yet — check back after the user starts learning.</p>
  </div>
</template>

<script>
import { computed } from 'vue'

const DAYS = [
  { key: 0, label: 'Sun' },
  { key: 1, label: 'Mon' },
  { key: 2, label: 'Tue' },
  { key: 3, label: 'Wed' },
  { key: 4, label: 'Thu' },
  { key: 5, label: 'Fri' },
  { key: 6, label: 'Sat' },
]

// Show every other hour to keep the grid compact
const HOURS = [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22]

const LEGEND_STEPS = [0, 1, 2, 3, 4, 5]

export default {
  name: 'EngagementHeatmap',

  props: {
    heatmap: {
      type: Array,
      default: () => [],
    },
  },

  setup(props) {
    // Build a fast lookup: { "hour-dow": count }
    const lookup = computed(() => {
      const map = {}
      for (const entry of (props.heatmap || [])) {
        const key = `${entry.hour}-${entry.dow}`
        map[key] = (map[key] || 0) + entry.count
      }
      return map
    })

    const maxCount = computed(() =>
      Math.max(1, ...Object.values(lookup.value))
    )

    const isEmpty = computed(() => Object.keys(lookup.value).length === 0)

    const cellCount = (hour, dow) => {
      // The backend returns hour (0-23) and dow (0=Sun in PostgreSQL's EXTRACT(DOW))
      // We display hours in 2-hour buckets — aggregate hour and hour+1
      const c = (lookup.value[`${hour}-${dow}`] || 0) +
                (lookup.value[`${hour + 1}-${dow}`] || 0)
      return c
    }

    const cellColor = (hour, dow) => {
      const count = cellCount(hour, dow)
      if (count === 0) return '#f8fafc'
      const intensity = Math.min(count / maxCount.value, 1)
      return interpolateColor(intensity)
    }

    const legendColor = (step) => {
      if (step === 0) return '#f8fafc'
      return interpolateColor(step / 5)
    }

    const interpolateColor = (t) => {
      // Light purple → deep purple gradient
      const r = Math.round(237 - t * (237 - 109))
      const g = Math.round(233 - t * (233 - 40))
      const b = Math.round(254 - t * (254 - 217))
      return `rgb(${r},${g},${b})`
    }

    const formatHour = (h) => {
      if (h === 0)  return '12am'
      if (h === 12) return '12pm'
      return h < 12 ? `${h}am` : `${h - 12}pm`
    }

    return { DAYS, HOURS, LEGEND_STEPS, isEmpty, cellCount, cellColor, legendColor, formatHour }
  }
}
</script>

<style scoped>
.engagement-heatmap { width: 100%; }

.heatmap-grid {
  display: grid;
  grid-template-columns: 36px repeat(7, 1fr);
  gap: 2px;
}

.cell {
  height: 18px;
  border-radius: 2px;
}

.cell--header {
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.62rem;
  font-weight: 600;
  color: #94a3b8;
  height: 20px;
}

.cell--hour-label {
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 4px;
  font-size: 0.58rem;
  color: #94a3b8;
}

.cell--data {
  cursor: default;
  transition: opacity 0.1s;
  border-radius: 2px;
}
.cell--data:hover { opacity: 0.8; }

.heatmap-legend {
  display: flex;
  align-items: center;
  gap: 3px;
  margin-top: 0.75rem;
  justify-content: flex-end;
}
.legend-label { font-size: 0.65rem; color: #94a3b8; }
.legend-cell {
  width: 16px;
  height: 12px;
  border-radius: 2px;
}

.no-data {
  text-align: center;
  color: #94a3b8;
  font-size: 0.8rem;
  padding: 1rem 0;
  font-style: italic;
}
</style>
